// src/components/settings/ContactOptionsSettings.tsx
import { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { supabase } from "@/lib/supabase";
import { captureError } from "@/lib/sentry";

interface ContactOption {
  id: string;
  category: string;
  value: string;
}

const CATEGORIES = [
  { id: 'relationship', label: 'Relationship Types' },
  { id: 'type', label: 'Contact Types' },
  { id: 'rank', label: 'Contact Ranks' },
  { id: 'status', label: 'Contact Statuses' },
  { id: 'call_outcome', label: 'Call Outcomes' },
];

export function ContactOptionsSettings() {
  const [options, setOptions] = useState<ContactOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0].id);
  const [newOption, setNewOption] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const canModify = currentWorkspace?.role === 'owner' || currentWorkspace?.role === 'admin';

  const fetchOptions = async () => {
    if (!currentWorkspace) return;

    try {
      const { data, error } = await supabase
        .from('contact_options')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('value');

      if (error) throw error;
      setOptions(data || []);
    } catch (error) {
      console.error('Error fetching options:', error);
      captureError(error as Error, { feature: 'contact-options-fetch' });
      toast({
        title: "Error",
        description: "Failed to load contact options",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentWorkspace) {
      fetchOptions();
    }
  }, [currentWorkspace?.id]);

  const addOption = async () => {
    if (!currentWorkspace || !newOption.trim()) return;

    try {
      const newOptionData = {
        workspace_id: currentWorkspace.id,
        category: selectedCategory,
        value: newOption.trim(),
      };

      const { data, error } = await supabase
        .from('contact_options')
        .insert([newOptionData])
        .select()
        .single();

      if (error) throw error;

      // Optimistically update the UI
      setOptions(prev => [...prev, data]);
      setNewOption('');
      setIsAdding(false);

      toast({
        description: "Option added successfully",
      });
    } catch (error: any) {
      console.error('Error adding option:', error);
      captureError(error as Error, { feature: 'contact-options-add' });
      toast({
        title: "Error",
        description: error.message === '23505'
          ? "This option already exists"
          : "Failed to add option",
        variant: "destructive",
      });
    }
  };

  const deleteOption = async (optionId: string) => {
    if (!currentWorkspace) return;

    try {
      // Optimistically update the UI
      setOptions(prev => prev.filter(opt => opt.id !== optionId));

      const { data, error } = await supabase
        .from('contact_options')
        .delete()
        .match({
          id: optionId,
          workspace_id: currentWorkspace.id,
        })
        .select();

      if (error) {
        await fetchOptions();
        throw error;
      }
      // RLS DELETE policy on contact_options requires owner/admin. A non-admin
      // delete returns no error but zero rows; treat that as a permission
      // failure so the UI doesn't lie about success.
      if (!data || data.length === 0) {
        await fetchOptions();
        throw new Error('You do not have permission to delete this option.');
      }

      toast({
        description: "Option deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting option:', error);
      captureError(error as Error, { feature: 'contact-options-delete' });
      toast({
        title: "Error",
        description: (error as Error)?.message ?? "Failed to delete option",
        variant: "destructive",
      });
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No workspace selected
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Contact Options</h2>
        <p className="text-muted-foreground">
          Manage the available options for contact details
        </p>
      </div>

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Select
            value={selectedCategory}
            onValueChange={setSelectedCategory}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(category => (
                <SelectItem key={category.id} value={category.id}>
                  {category.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {canModify && (
            <Button
              variant="outline"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </Button>
          )}
        </div>

        {isAdding && canModify && (
          <div className="flex items-center gap-2">
            <Input
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              placeholder="Enter new option"
              className="max-w-sm"
            />
            <Button 
              onClick={addOption}
              disabled={!newOption.trim()}
            >
              Add
            </Button>
            <Button 
              variant="ghost"
              onClick={() => {
                setIsAdding(false);
                setNewOption('');
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-4">Loading options...</div>
        ) : (
          <div className="space-y-6">
            {CATEGORIES.map(category => {
              const categoryOptions = options.filter(o => o.category === category.id);
              
              return (
                <div key={category.id} className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    {category.label}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {categoryOptions.length > 0 ? (
                      categoryOptions.map(option => (
                        <Badge
                          key={option.id}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {option.value}
                          {canModify && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={() => deleteOption(option.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </Badge>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        No options defined
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
